/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiltersResponse } from '../config/api';
// import DateRangeCalendar from './DateRangeCalendar';
import DateRangeCalendar from './MonthRangeCalendar';
import { useFilters } from '../contexts/FiltersContext';


const CATEGORIES = {
  time        : {label: 'Time', icon: 'fa-calendar-alt', filters: []},
  audience    : {label: 'Audience', icon: 'fa-users', filters: ['data_source', 'channel']},
  demographics: {label: 'Demographics', icon: 'fa-users', filters: ['age', 'gender', 'nationality']},
  geographic  : {label: 'Geographic', icon: 'fa-map-marker-alt', filters: ['country', 'city']},
  economic    : {label: 'Economic', icon: 'fa-chart-line', filters: ['sec']},
}


interface DemographicFilterProps {
  onFiltersChange: (filters: FiltersResponse[]) => void;
  availableFilters?: FiltersResponse[]|null;
}

/**
 * Advanced demographic filtering component for brand health data
 */

const DemographicFilter: React.FC<DemographicFilterProps> = ({
  onFiltersChange,
  availableFilters
}) => {
  // const { startDate, endDate, setDateRange, minDate, maxDate } = useDateFilter();
  const { isFilterEnabled, routeType } = useFilters()
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('time');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<FiltersResponse[]>([]);

  const handleDateRangeChange = (start?: Date, end?: Date) => {
    setSelectedFilters(prev => {
      let timeFiltersId = prev.findIndex(filter => filter.field === 'time')
      if (start && end && start.getTime() !== end.getTime()) {
        if (timeFiltersId !== -1) {
            return prev.map(filter => filter.field === 'time'? {...filter, values: [start.toISOString().split('T')[0], end.toISOString().split('T')[0]]}: filter)
        } else {
          const filterType = availableFilters?.find(f => f.field === 'time')?.type || "common"
          return [...prev, {field: 'time', values: [start.toISOString().split('T')[0], end.toISOString().split('T')[0]], type: filterType}]
        }
      } else if (start && end || !start && !end) {
        return prev.map(filter => filter.field === 'time'? {...filter, values: []}: filter)
      }
      return prev
    })
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterCategories = useMemo(() => 
    Object.entries(CATEGORIES).map(([category, {filters, ...rest}]) => ({
      ...rest,
      id: category,
      filters: filters.filter(isFilterEnabled)
    })).filter(cat => cat.id === 'time' || cat.filters.length > 0)
  , [isFilterEnabled])

  const toggleFilter = (category: string, value: string) => {
    const newFilters = [...selectedFilters]
    let categoryFiltersId = newFilters.findIndex(filter => filter.field === category);
    
    if (categoryFiltersId !== -1) {
      let categoryFilters = {...newFilters[categoryFiltersId]}
      if (categoryFilters.values.includes(value)) {
        categoryFilters.values = categoryFilters.values.filter(v => v !== value);
      } else {
        categoryFilters.values = [...categoryFilters.values, value];
      }
      newFilters[categoryFiltersId] = categoryFilters
    } else {
      const filterType = availableFilters?.find(f => f.field === category)?.type || "common"
      newFilters.push({field: category, values: [value], type: filterType})
    }
    
    setSelectedFilters(newFilters);
    // onFiltersChange(newFilters);
  };

  const applyFilters = () => {
    onFiltersChange(selectedFilters)
    setIsOpen(false)
  }

  const clearFilters = () => {
    const emptyFilters: FiltersResponse[] = [];
    setSelectedFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  const getActiveFilterCount = () => {
    return selectedFilters.filter(f => isFilterEnabled(f.field)).reduce((acc, f) => f.values.length + acc, 0)
  };

  const getFilterLabel = (key: string): string => {
    const labels: Record<string, string> = {
      age: 'Age Group',
      gender: 'Gender',
      nationality: 'Nationality',
      city: 'City',
      sec: 'Socio-Economic Class',
      decisionMaker: 'Purchase Decision Maker',
      householdCars: 'Household Cars',
      residenceType: 'Residence Type'
    };
    return labels[key] || key;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Filter Button */}
      <button
        disabled={!availableFilters || availableFilters.length === 0}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
      >
        <i className="fas fa-sliders-h text-text-muted text-xs"></i>
        <span className="text-foreground text-sm">Filters</span>
        {getActiveFilterCount() > 0 && (
          <span className="w-4 h-4 bg-primary text-white text-[10px] rounded-full flex items-center justify-center">
            {getActiveFilterCount()}
          </span>
        )}
      </button>

      {/* Filter Dropdown Panel */}
      {isOpen && availableFilters && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-dropdown max-h-[700px] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-[var(--border)]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">Advanced Filters</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-background-tertiary rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-text-muted" />
                </button>
              </div>

              {getActiveFilterCount() > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-text-muted">
                    {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
                  </span>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Category Tabs */}
            <div className="flex overflow-x-auto border-b border-[var(--border)] scrollbar-hide min-h-[60px]">
              {filterCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveTab(category.id)}
                  className={`flex-1 px-4 py-2 pb-5 text-sm font-medium transition-all ${
                    activeTab === category.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5'
                      : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
                  }`}
                >
                  <i className={`fas ${category.icon} mr-2`}></i>
                  {category.label}
                </button>
              ))}
            </div>

            {/* Filter Options */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Time Period / Calendar View */}
              {activeTab === 'time' && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-4">
                    Select Date Range
                  </h3>
                  {(() => {
                    const availableFilter = availableFilters.find(f => f.field === 'time')!
                    const timeFilter = selectedFilters.find(f => f.field === 'time')

                    if (!timeFilter) {
                      return (
                        <DateRangeCalendar
                          minDate={new Date(availableFilter.values[0])}
                          maxDate={new Date(availableFilter.values[1])}
                          onRangeChange={handleDateRangeChange}
                        />
                      )
                    }

                    return (
                      <DateRangeCalendar
                        startDate={new Date(timeFilter.values[0])}
                        endDate={new Date(timeFilter.values[1])}
                        minDate={new Date(availableFilter.values[0])}
                        maxDate={new Date(availableFilter.values[1])}
                        onRangeChange={handleDateRangeChange}
                      />
                    )
                  })()}
                  {/* selectedFilters.findIndex(f => f.field === 'time') > -1? (
                    <DateRangeCalendar
                      startDate={new Date(selectedFilters.find(f => f.field === 'time')![0])}
                      endDate={new Date(selectedFilters.find(f => f.field === 'time')![1])}
                      minDate={new Date(availableFilters.find(f => f.field === 'time')![0])}
                      maxDate={new Date(availableFilters.find(f => f.field === 'time')![1])}
                      onRangeChange={handleDateRangeChange}
                    />
                  ):(
                    <DateRangeCalendar
                      minDate={new Date(availableFilters.time[0])}
                      maxDate={new Date(availableFilters.time[1])}
                      onRangeChange={handleDateRangeChange}
                    />
                  ) */}
                </div>
              )}

              {/* Other Filter Tabs */}
              {activeTab !== 'time' && filterCategories.find(cat => cat.id === activeTab)!.filters.map(filterKey => {
                const key = filterKey;
                const available = availableFilters.find(f => f.field === key);
                if (!available) throw "Unreachable: the available filters should be validated beforehand."

                const selected = selectedFilters.find(f => f.field === key);

                return (
                  <div key={filterKey} className="mb-6">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase mb-3">
                      {getFilterLabel(filterKey)} {routeType === 'common' && available.type !== 'common'? (<span className="font-thin text-red text-xs" style={{color: '#f30c'}}>(For {available.type} only)</span>): null}
                    </h3>
                    <div className="space-y-2">
                      {[...new Set(available.values)].map(option => (
                        <label
                          key={option}
                          className="flex items-center space-x-3 cursor-pointer hover:bg-background-tertiary p-2 rounded-lg transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selected?.values.includes(option) || false}
                            onChange={() => toggleFilter(key, option)}
                            className="w-4 h-4 text-primary bg-background-tertiary border-[var(--border)] rounded focus:ring-primary focus:ring-2"
                          />
                          <span className="text-foreground text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Apply Button */}
            <div className="p-6 border-t border-[var(--border)]">
              <button
                onClick={applyFilters}
                className="w-full py-3 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-all font-medium"
              >
                Apply Filters
              </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default DemographicFilter;
